import { globalEvent } from '@shared/easyfy.core';
import { GetAgeGate } from '../services/agegate.service';
import { FeatureNames, Features } from '@webServices/features.service';

export default class AgeGate {
  static Instance: AgeGate;
  // Cookie consent banner; must stay usable above the age gate.
  private static readonly CONSENT_SELECTOR = '#onetrust-consent-sdk';
  private dialog: HTMLDialogElement | null = null;
  private inertElements: HTMLElement[] = [];
  private backgroundObserver: MutationObserver | null = null;
  private previouslyFocused: HTMLElement | null = null;

  constructor() {
    AgeGate.Instance = this;
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'age-gate-accept',
          callback: this.acceptAgeGate,
        },
      ],
    });

    this.checkCookie();
  }

  checkCookie = () => {
    if (!this.checkIfActiveFeature()) return;

    const cookie = document.cookie
      ?.split('; ')
      ?.find((row) => row.startsWith('AgeGate'))
      ?.split('=')[1];

    if (!cookie) {
      setTimeout(() => {
        AgeGate.Instance.loadAgeGate();
      }, 5000);
    }
  };

  loadAgeGate = async () => {
    const response = await GetAgeGate();
    if (!response.data?.partialHtml) return;

    document.body.insertAdjacentHTML('beforeend', response.data.partialHtml);
    const dialog = document.querySelector('[data-container="age-gate"]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.dialog = dialog;
    this.openDialog(dialog);
  };

  openDialog = (dialog: HTMLDialogElement) => {
    // Capture before inert is applied, since inert blurs a focused element in the background.
    const focused = document.activeElement;
    this.previouslyFocused = focused instanceof HTMLElement && focused !== document.body ? focused : null;
    const focusInConsent = !!this.previouslyFocused && !!document.querySelector(AgeGate.CONSENT_SELECTOR)?.contains(this.previouslyFocused);

    document.body.classList.add('age-gate-is-active');
    // Non-modal so the consent banner stays above the age gate; inert covers the rest of the page.
    this.makeBackgroundInert(dialog);
    dialog.show();
    // show() focuses the first focusable descendant, not the dialog itself (autofocus isn't honoured consistently);
    // focus the dialog so its title announce first, unless the consent banner already had focus.
    if (focusInConsent) this.previouslyFocused?.focus();
    else dialog.focus();
  };

  onConsentFocusOut = (event: FocusEvent) => {
    // Recover focus only when it was dropped by OneTrust hiding the banner, not when the user moved on.
    const { target } = event;
    if (!this.dialog?.open || event.relatedTarget || !(target instanceof Element)) return;
    if (!target.closest(AgeGate.CONSENT_SELECTOR) || target.getClientRects().length > 0) return;

    this.dialog.focus();
  };

  acceptAgeGate = () => {
    document.cookie = 'AgeGate=true';

    this.dialog?.remove();
    this.dialog = null;
    this.releaseBackground();
    document.body.classList.remove('age-gate-is-active');
    this.restoreFocus();
  };

  restoreFocus = () => {
    // Removing the dialog drops focus to <body>; return it to where the user was, or to the top of the page.
    const previous = this.previouslyFocused;
    this.previouslyFocused = null;
    if (previous?.isConnected) {
      previous.focus({ preventScroll: true });
      if (document.activeElement === previous) return;
    }

    // Focus <body> explicitly so the next Tab starts at the top (skip link, menu) rather than where the removed dialog was.
    const { body } = document;
    if (!body.hasAttribute('tabindex')) {
      body.setAttribute('tabindex', '-1');
      body.addEventListener('blur', () => body.removeAttribute('tabindex'), { once: true });
    }
    body.focus({ preventScroll: true });
  };

  makeBackgroundInert = (dialog: HTMLDialogElement) => {
    const makeInert = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node === dialog || node.matches(AgeGate.CONSENT_SELECTOR) || node.hasAttribute('inert')) return;

      node.setAttribute('inert', '');
      this.inertElements.push(node);
    };

    Array.from(document.body.children).forEach(makeInert);

    // Catches third-party widgets (e.g. chat) appended while the age gate is open.
    this.backgroundObserver = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach(makeInert)));
    this.backgroundObserver.observe(document.body, { childList: true });
    document.addEventListener('focusout', this.onConsentFocusOut);
  };

  releaseBackground = () => {
    document.removeEventListener('focusout', this.onConsentFocusOut);
    this.backgroundObserver?.disconnect();
    this.backgroundObserver = null;
    this.inertElements.forEach((element) => element.removeAttribute('inert'));
    this.inertElements = [];
  };

  checkIfActiveFeature = (): boolean => {
    return Features.FeatureIsActive(FeatureNames.AgeGate);
  };
}
